import React, { useState, useContext, useMemo } from 'react';
import { Button, Container, Row, Col, Dropdown } from 'react-bootstrap';

import { CartContext } from '../context/Cart';

import '../styles/components/selectableVariants.css';

function parseVariantOptions(variants) {
  // Shopify variant titles follow "Option1 / Option2 / ..." format
  const optionCount = variants[0]?.title.split(' / ').length || 1;

  if (optionCount <= 1) return null;

  // Extract unique values for each option position
  const optionGroups = [];
  for (let i = 0; i < optionCount; i++) {
    const seen = new Set();
    const values = [];
    for (const variant of variants) {
      const parts = variant.title.split(' / ');
      const val = parts[i]?.trim();
      if (val && !seen.has(val)) {
        seen.add(val);
        values.push(val);
      }
    }
    optionGroups.push(values);
  }

  return optionGroups;
}

const SIZE_VALUES = new Set([
  'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', '3xl',
  'small', 'medium', 'large',
  'one size', 'os', 'o/s',
]);

function isSizeGroup(values) {
  return values.some(v => SIZE_VALUES.has(v.toLowerCase()));
}

function findVariant(variants, selections) {
  const target = selections.join(' / ');
  return variants.find(v => v.title === target) || null;
}

const SelectableVariants = ({ variants }) => {
  const availableVariants = variants.filter(variant => variant.available);
  const allVariants = variants;

  const optionGroups = useMemo(() => parseVariantOptions(allVariants), [allVariants]);

  const { addToCart } = useContext(CartContext);

  // For multi-option products, track each selection independently
  const initialSelections = useMemo(() => {
    if (!optionGroups) return null;
    // Default to the first available variant's options
    if (availableVariants.length > 0) {
      return availableVariants[0].title.split(' / ').map(s => s.trim());
    }
    return optionGroups.map(group => group[0]);
  }, [optionGroups, availableVariants]);

  const [selections, setSelections] = useState(initialSelections);
  const [selectedVariant, setSelectedVariant] = useState(
    optionGroups ? null : (availableVariants[0] || null)
  );

  // For multi-option: derive the selected variant from current selections
  const resolvedVariant = useMemo(() => {
    if (!optionGroups || !selections) return selectedVariant;
    return findVariant(allVariants, selections);
  }, [optionGroups, selections, allVariants, selectedVariant]);

  const isAvailable = resolvedVariant?.available;

  const handleOptionChange = (groupIndex, value) => {
    const next = [...selections];
    next[groupIndex] = value;
    setSelections(next);
  };

  const handleAddToCart = () => {
    if (resolvedVariant && resolvedVariant.available) {
      addToCart(resolvedVariant);
    }
  };

  if (availableVariants.length === 0) {
    return (
      <Container>
        <Row>
          <Col md={12}>
            <p className="text-muted soldout-text">SOLD OUT</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Multi-option layout (e.g., Color + Size)
  // Detect which group is sizes and which is colors
  if (optionGroups && selections) {
    const sizeIdx = optionGroups.findIndex(g => isSizeGroup(g));
    const colorIdx = sizeIdx === 0 ? 1 : 0;

    return (
      <Container>
        <Row>
          {/* Color buttons (shown first) */}
          <Col md={12} className="mb-2">
            <div className="color-options">
              {optionGroups[colorIdx].map((value) => (
                <Button
                  key={value}
                  size="sm"
                  className="color-option-btn"
                  variant={selections[colorIdx] === value ? 'dark' : 'outline-dark'}
                  onClick={() => handleOptionChange(colorIdx, value)}
                >
                  {value}
                </Button>
              ))}
            </div>
          </Col>
          {/* Size dropdown */}
          {sizeIdx >= 0 && (
            <Col md={12}>
              <Dropdown drop="up" className="mb-2">
                <Dropdown.Toggle
                  size="sm"
                  className="product-variant-dropdown"
                  variant="outline-dark"
                  id="dropdown-size"
                >
                  {selections[sizeIdx]}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {optionGroups[sizeIdx].map((value) => (
                    <Dropdown.Item
                      key={value}
                      active={selections[sizeIdx] === value}
                      onClick={() => handleOptionChange(sizeIdx, value)}
                    >
                      {value}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          )}
          <Col md={12}>
            <Button
              className="add-to-cart-btn mt-2 mb-2"
              onClick={handleAddToCart}
              variant="dark"
              disabled={!resolvedVariant || !isAvailable}
              size="sm"
            >
              {resolvedVariant && !isAvailable ? 'Sold Out' : 'Add to Cart'}
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  // Single-option layout (original behavior)
  return (
    <Container>
      <Row>
        <Col md={12}>
          <Dropdown drop="up">
            <Dropdown.Toggle size="sm" className="product-variant-dropdown" variant="outline-dark" id="dropdown-basic">
              {selectedVariant?.title || "Select an option"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {availableVariants.map((variant, variantIdx) => (
                <Dropdown.Item
                  key={variantIdx}
                  onClick={() => setSelectedVariant(variant)}
                >
                  {variant.title}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col md={12}>
          <Button
            className="add-to-cart-btn mt-2 mb-2"
            onClick={() => addToCart(selectedVariant)}
            variant="dark"
            disabled={!selectedVariant}
            size="sm"
          >
            Add to Cart
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default SelectableVariants;
